import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { Plus, Trash2, ScanLine, Download, Printer, CreditCard, Wallet, Search } from "lucide-react";
import { toast } from "sonner";
import AnalyticsView from "../components/AnalyticsView";
import { fmtMoney, fmtInt } from "../lib/format";

/** Tiny helper that escapes a CSV field per RFC 4180. */
function csvField(v) {
  const s = v === null || v === undefined ? "" : String(v);
  if (s.includes(",") || s.includes('"') || s.includes("\n") || s.includes("\r")) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
}

/** Build a downloadable CSV blob from the sales list + trigger the browser download. */
function downloadSalesCsv(eventName, sales) {
  const header = [
    "Sale ID", "Timestamp", "Product", "SKU", "Variant", "Qty", "Unit price",
    "Total", "Payment method", "Square payment id", "Sold by",
  ];
  const rows = sales.map((s) => [
    s.sale_id,
    new Date(s.created_at).toISOString(),
    s.product_name,
    s.sku,
    s.variant_attributes
      ? Object.entries(s.variant_attributes).map(([k, v]) => `${k}: ${v}`).join(" / ")
      : "",
    s.quantity,
    s.unit_price,
    s.total,
    s.payment_method || "cash",
    s.square_payment_id || "",
    s.created_by_name || "",
  ]);
  const body = [header, ...rows].map((r) => r.map(csvField).join(",")).join("\r\n");
  const safeName = (eventName || "event").replace(/[^a-z0-9_-]+/gi, "_").toLowerCase();
  const date = new Date().toISOString().slice(0, 10);
  const blob = new Blob([body], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `vendor-vault_${safeName}_${date}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export default function EventDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [events, setEvents] = useState([]);
  const [sales, setSales] = useState([]);
  const [products, setProducts] = useState([]);
  const [payouts, setPayouts] = useState(null);
  const [daily, setDaily] = useState(null);
  const [form, setForm] = useState({ product_id: "", variant_id: "", quantity: 1, unit_price: "" });
  const [saleSearch, setSaleSearch] = useState("");
  const [paymentFilter, setPaymentFilter] = useState("all"); // all | cash | square

  const load = async () => {
    const calls = [
      api.get("/events"),
      api.get(`/sales?event_id=${id}`),
      api.get("/products"),
      api.get(`/events/${id}/daily`),
    ];
    if (isAdmin) calls.push(api.get(`/events/${id}/payouts`));
    const results = await Promise.all(calls);
    setEvents(results[0].data); setSales(results[1].data); setProducts(results[2].data);
    setDaily(results[3].data);
    if (isAdmin && results[4]) setPayouts(results[4].data);
  };
  useEffect(() => { load(); }, [id]);

  const event = events.find((e) => e.event_id === id);
  const selectedProduct = products.find((p) => p.product_id === form.product_id);

  const totalRevenue = useMemo(() => sales.reduce((a, s) => a + Number(s.total||0), 0), [sales]);

  const filteredSales = useMemo(() => {
    const q = saleSearch.trim().toLowerCase();
    return sales.filter((s) => {
      const method = s.payment_method || "cash";
      if (paymentFilter !== "all" && method !== paymentFilter) return false;
      if (!q) return true;
      return (
        (s.product_name || "").toLowerCase().includes(q)
        || (s.sku || "").toLowerCase().includes(q)
        || (s.created_by_name || "").toLowerCase().includes(q)
      );
    });
  }, [sales, saleSearch, paymentFilter]);

  const paymentTotals = useMemo(() => {
    const out = { cash: 0, square: 0, other: 0 };
    for (const s of sales) {
      const m = s.payment_method || "cash";
      if (m === "square") out.square += Number(s.total || 0);
      else if (m === "cash") out.cash += Number(s.total || 0);
      else out.other += Number(s.total || 0);
    }
    return out;
  }, [sales]);

  const logSale = async (e) => {
    e.preventDefault();
    if (!form.product_id || !form.variant_id) { toast.error("Pick a product & variant"); return; }
    try {
      await api.post("/sales", {
        event_id: id,
        product_id: form.product_id,
        variant_id: form.variant_id,
        quantity: Number(form.quantity) || 1,
        unit_price: form.unit_price === "" ? undefined : Number(form.unit_price),
      });
      toast.success("Sale logged");
      setForm({ ...form, quantity: 1, unit_price: "" });
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };

  const removeSale = async (sid) => {
    if (!window.confirm("Delete this sale? Stock will be restored.")) return;
    await api.delete(`/sales/${sid}`);
    load();
  };

  return (
    <div className="space-y-6" data-testid="event-detail-page">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        <div>
          <div className="font-mono text-xs uppercase tracking-widest text-[#4A4A4A]">Show</div>
          <h1 className="font-heading text-3xl sm:text-4xl md:text-5xl font-black uppercase tracking-tight">{event?.name || "Event"}</h1>
          <div className="font-mono text-xs text-[#4A4A4A] mt-1">{event?.location} · {event?.start_date}</div>
          {isAdmin && event && <EventTaxEditor event={event} onSaved={(ev) => setEvents((es)=>es.map((x)=>x.event_id===ev.event_id?ev:x))} />}
        </div>
        <div className="flex flex-wrap gap-2">
          <Link to={`/scanner?event_id=${id}`} className="brutal-btn" data-testid="event-scanner-btn">
            <ScanLine size={14}/> Sell at this Event
          </Link>
          <Link to="/events" className="brutal-btn brutal-btn-secondary">Back</Link>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="brutal-card p-5"><div className="font-mono text-[10px] uppercase tracking-widest">Revenue</div><div className="font-mono text-3xl font-bold">{fmtMoney(totalRevenue)}</div></div>
        <div className="brutal-card p-5"><div className="font-mono text-[10px] uppercase tracking-widest">Sales</div><div className="font-mono text-3xl font-bold">{fmtInt(sales.length)}</div></div>
        <div className="brutal-card p-5"><div className="font-mono text-[10px] uppercase tracking-widest">Units</div><div className="font-mono text-3xl font-bold">{fmtInt(sales.reduce((a,s)=>a+s.quantity,0))}</div></div>
      </div>

      <form onSubmit={logSale} className="brutal-card p-6 grid grid-cols-1 md:grid-cols-5 gap-3 no-print">
        <div>
          <label className="brutal-label">Product</label>
          <select className="brutal-input" value={form.product_id} onChange={(e)=>setForm({...form, product_id: e.target.value, variant_id: ""})} data-testid="sale-product-select">
            <option value="">—</option>
            {products.map((p) => <option key={p.product_id} value={p.product_id}>{p.name}</option>)}
          </select>
        </div>
        <div>
          <label className="brutal-label">Variant</label>
          <select className="brutal-input" value={form.variant_id} onChange={(e)=>setForm({...form, variant_id: e.target.value})} data-testid="sale-variant-select">
            <option value="">—</option>
            {selectedProduct?.variants?.map((v) => (
              <option key={v.variant_id} value={v.variant_id}>
                {v.sku} · stock {v.stock}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="brutal-label">Qty</label>
          <input type="number" min="1" className="brutal-input" value={form.quantity} onChange={(e)=>setForm({...form, quantity: e.target.value})}/>
        </div>
        <div>
          <label className="brutal-label">Price override</label>
          <input type="number" step="0.01" className="brutal-input" placeholder="default" value={form.unit_price} onChange={(e)=>setForm({...form, unit_price: e.target.value})}/>
        </div>
        <div className="flex items-end">
          <button className="brutal-btn w-full" data-testid="log-event-sale-btn"><Plus size={14}/> Log Sale</button>
        </div>
      </form>

      {/* ============================================================
          Sale by Sale Report — the canonical event ledger.
          Shows every sale (one row each), filterable by payment method
          + free-text search, with CSV / Print export.
          On mobile, switches from table to card layout.
       ============================================================ */}
      <section className="brutal-card p-0 overflow-hidden" data-testid="sale-by-sale-section" id="sale-by-sale-report">
        <header className="p-5 border-b-2 border-black bg-[#FFF5D6] flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-[160px]">
            <div className="font-mono text-[10px] uppercase tracking-widest text-[#4A4A4A]">Ledger</div>
            <h2 className="font-heading text-xl sm:text-2xl font-black uppercase">Sale by Sale Report</h2>
            <div className="font-mono text-[11px] text-[#4A4A4A] mt-1">
              {fmtInt(sales.length)} sales · {fmtMoney(totalRevenue)} revenue
              {paymentTotals.square > 0 && (
                <> · <span className="font-bold">Square</span> {fmtMoney(paymentTotals.square)}</>
              )}
              {paymentTotals.cash > 0 && (
                <> · <span className="font-bold">Cash</span> {fmtMoney(paymentTotals.cash)}</>
              )}
            </div>
          </div>
          <div className="flex flex-wrap gap-2 no-print">
            <button
              type="button"
              onClick={()=>downloadSalesCsv(event?.name, filteredSales)}
              disabled={filteredSales.length === 0}
              className="brutal-btn brutal-btn-secondary !py-1 !px-2 text-xs inline-flex items-center gap-1 disabled:opacity-30"
              data-testid="sales-csv-btn"
            >
              <Download size={12}/> CSV
            </button>
            <button
              type="button"
              onClick={()=>window.print()}
              disabled={filteredSales.length === 0}
              className="brutal-btn brutal-btn-secondary !py-1 !px-2 text-xs inline-flex items-center gap-1 disabled:opacity-30"
              data-testid="sales-print-btn"
            >
              <Printer size={12}/> Print
            </button>
          </div>
        </header>

        {/* Controls: search + payment-method filter */}
        <div className="px-4 pt-4 flex flex-wrap gap-2 no-print">
          <div className="relative flex-1 min-w-[180px]">
            <Search size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-[#4A4A4A]"/>
            <input
              type="text"
              value={saleSearch}
              onChange={(e)=>setSaleSearch(e.target.value)}
              placeholder="Filter by product, SKU, or seller…"
              className="brutal-input !pl-7 !py-1 text-xs w-full"
              data-testid="sale-search-input"
            />
          </div>
          <div className="inline-flex border-2 border-black" data-testid="sale-payment-filter">
            {[
              { key: "all", label: "All" },
              { key: "cash", label: "Cash" },
              { key: "square", label: "Square" },
            ].map((opt) => (
              <button
                key={opt.key}
                type="button"
                onClick={()=>setPaymentFilter(opt.key)}
                className={`font-mono text-[10px] uppercase tracking-widest px-2 py-1 ${paymentFilter === opt.key ? "bg-black text-white" : "bg-white hover:bg-[#F4F2EC]"}`}
                data-testid={`sale-pf-${opt.key}`}
              >{opt.label}</button>
            ))}
          </div>
        </div>

        {/* MOBILE: card list */}
        <div className="md:hidden p-4 space-y-2" data-testid="sales-cards">
          {filteredSales.length === 0 && (
            <div className="text-center font-mono text-xs py-8 text-[#4A4A4A]">
              {sales.length === 0 ? "No sales logged yet." : "No sales match this filter."}
            </div>
          )}
          {filteredSales.map((s) => {
            const method = s.payment_method || "cash";
            return (
              <div
                key={s.sale_id}
                className="border-2 border-black bg-white p-3 space-y-1"
                data-testid={`sale-card-${s.sale_id}`}
              >
                <div className="flex items-start gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="font-mono text-xs font-bold truncate">{s.product_name}</div>
                    <div className="font-mono text-[10px] text-[#4A4A4A]">
                      {s.sku ? `${s.sku} · ` : ""}{fmtInt(s.quantity)} × {fmtMoney(s.unit_price)}
                    </div>
                  </div>
                  <div className="text-right whitespace-nowrap">
                    <div className="font-mono text-sm font-black">{fmtMoney(s.total)}</div>
                    <span className={`inline-flex items-center gap-1 font-mono text-[9px] uppercase tracking-widest px-1.5 py-0.5 border border-black ${method === "square" ? "bg-[#111] text-white" : "bg-[#F4F2EC]"}`}>
                      {method === "square" ? <CreditCard size={9}/> : <Wallet size={9}/>} {method}
                    </span>
                  </div>
                </div>
                <div className="flex items-center justify-between font-mono text-[10px] text-[#4A4A4A]">
                  <span>{new Date(s.created_at).toLocaleString()}</span>
                  {isAdmin && s.created_by_name && <span className="truncate ml-2">By {s.created_by_name}</span>}
                </div>
                {isAdmin && (
                  <div className="pt-1">
                    <button
                      onClick={()=>removeSale(s.sale_id)}
                      className="brutal-btn brutal-btn-secondary !py-1 !px-2 text-[10px] inline-flex items-center gap-1 no-print"
                      data-testid={`sale-delete-${s.sale_id}`}
                    ><Trash2 size={10}/> Remove (restocks)</button>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* DESKTOP: full table */}
        <div className="hidden md:block overflow-x-auto p-4">
          <table className="brutal-table">
            <thead><tr>
              <th>Time</th><th>Product</th><th>SKU</th><th>Qty</th>
              <th>Unit</th><th>Total</th><th>Payment</th>
              {isAdmin && <th>By</th>}<th className="no-print"></th>
            </tr></thead>
            <tbody>
              {filteredSales.length === 0 && (
                <tr><td colSpan={isAdmin ? 9 : 8} className="text-center font-mono text-sm py-6 text-[#4A4A4A]">
                  {sales.length === 0 ? "No sales logged yet." : "No sales match this filter."}
                </td></tr>
              )}
              {filteredSales.map((s) => {
                const method = s.payment_method || "cash";
                return (
                  <tr key={s.sale_id} data-testid={`sale-row-${s.sale_id}`}>
                    <td className="font-mono text-xs">{new Date(s.created_at).toLocaleString()}</td>
                    <td>{s.product_name}</td>
                    <td className="font-mono text-xs">{s.sku}</td>
                    <td>{fmtInt(s.quantity)}</td>
                    <td>{fmtMoney(s.unit_price)}</td>
                    <td className="font-bold">{fmtMoney(s.total)}</td>
                    <td>
                      <span className={`inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-widest px-1.5 py-0.5 border border-black ${method === "square" ? "bg-[#111] text-white" : "bg-[#F4F2EC]"}`}>
                        {method === "square" ? <CreditCard size={10}/> : <Wallet size={10}/>} {method}
                      </span>
                    </td>
                    {isAdmin && <td className="font-mono text-xs">{s.created_by_name || "—"}</td>}
                    <td className="no-print">
                      {isAdmin && (
                        <button
                          className="brutal-badge !bg-[#D9381E] !text-white"
                          onClick={()=>removeSale(s.sale_id)}
                          aria-label={`Remove sale of ${s.product_name}`}
                        ><Trash2 size={12}/></button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>

      {isAdmin && (
        <AnalyticsView
          eventId={id}
          title="Event analytics"
          defaultRange="all"
          showExport={true}
        />
      )}

      {daily && daily.rows.length > 0 && (
        <div className="brutal-card p-6" data-testid="daily-section">
          <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
            <div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-[#4A4A4A]">Day-by-day</div>
              <h2 className="font-heading text-xl font-black uppercase">Sales Report</h2>
            </div>
            {daily.best_day && (
              <span className="brutal-badge !bg-[#C2410C] !text-white" data-testid="best-day-badge">
                Best day: {daily.best_day}
              </span>
            )}
          </div>

          <div className="space-y-2 mb-5">
            {daily.rows.map((r) => {
              const max = Math.max(...daily.rows.map((x) => x.revenue), 1);
              const pct = (r.revenue / max) * 100;
              return (
                <div key={r.date} className="flex items-center gap-3" data-testid={`day-bar-${r.date}`}>
                  <div className="font-mono text-[11px] uppercase tracking-widest w-24 sm:w-28 flex-shrink-0">
                    {r.date}
                  </div>
                  <div className="flex-1 bg-[#EAE7DF] border-2 border-black h-6 sm:h-7 relative overflow-hidden">
                    <div
                      className="absolute inset-y-0 left-0 bg-[#C2410C] transition-all"
                      style={{ width: `${Math.max(2, pct)}%` }}
                    />
                    <div className="absolute inset-0 flex items-center justify-end pr-2 font-mono text-[11px] font-bold mix-blend-difference text-white">
                      {fmtMoney(r.revenue, { compact: true })}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="overflow-x-auto">
            <table className="brutal-table">
              <thead><tr>
                <th>Date</th><th>Sales</th><th>Units</th><th>Revenue</th>
                {isAdmin && daily.cogs_known && <th>Cost</th>}
                {isAdmin && daily.cogs_known && <th>Profit</th>}
              </tr></thead>
              <tbody>
                {daily.rows.map((r) => (
                  <tr key={r.date} data-testid={`day-row-${r.date}`}>
                    <td className="font-mono text-xs">{r.date}</td>
                    <td className="font-mono text-xs">{fmtInt(r.sales)}</td>
                    <td className="font-mono text-xs">{fmtInt(r.units)}</td>
                    <td className="font-mono text-sm font-bold">{fmtMoney(r.revenue)}</td>
                    {isAdmin && daily.cogs_known && <td className="font-mono text-xs">{fmtMoney(r.cost)}</td>}
                    {isAdmin && daily.cogs_known && (
                      <td className={`font-mono text-sm font-bold ${r.profit >= 0 ? "text-[#4A7C59]" : "text-[#D9381E]"}`}>
                        {fmtMoney(r.profit)}
                      </td>
                    )}
                  </tr>
                ))}
                <tr className="bg-[#F4F2EC]">
                  <td className="font-heading font-black uppercase">Total</td>
                  <td className="font-mono text-xs font-bold">{fmtInt(daily.totals.sales)}</td>
                  <td className="font-mono text-xs font-bold">{fmtInt(daily.totals.units)}</td>
                  <td className="font-mono text-sm font-black">{fmtMoney(daily.totals.revenue)}</td>
                  {isAdmin && daily.cogs_known && <td className="font-mono text-xs font-bold">{fmtMoney(daily.totals.cost)}</td>}
                  {isAdmin && daily.cogs_known && (
                    <td className={`font-mono text-sm font-black ${daily.totals.profit >= 0 ? "text-[#4A7C59]" : "text-[#D9381E]"}`}>
                      {fmtMoney(daily.totals.profit)}
                    </td>
                  )}
                </tr>
              </tbody>
            </table>
          </div>

          {isAdmin && !daily.cogs_known && (
            <div className="mt-3 font-mono text-[10px] uppercase tracking-widest text-[#4A4A4A]">
              Tip: add a Cost on each variant to see daily profit here.
            </div>
          )}
        </div>
      )}

      {isAdmin && payouts && payouts.rows.length > 0 && (
        <div className="brutal-card p-6" data-testid="payouts-section">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-heading text-xl font-black uppercase">Per-Seller Payouts</h2>
            <span className="brutal-badge">{payouts.rows.length} seller{payouts.rows.length>1?"s":""}</span>
          </div>
          <div className="overflow-x-auto">
            <table className="brutal-table">
              <thead><tr><th>Seller</th><th>Sales</th><th>Units</th><th>Revenue</th><th>Share</th></tr></thead>
              <tbody>
                {payouts.rows.map((r) => (
                  <tr key={r.user_id} data-testid={`payout-row-${r.user_id}`}>
                    <td className="font-heading font-bold">{r.name}</td>
                    <td className="font-mono text-xs">{fmtInt(r.sales)}</td>
                    <td className="font-mono text-xs">{fmtInt(r.units)}</td>
                    <td className="font-mono text-sm font-bold">{fmtMoney(r.revenue)}</td>
                    <td>
                      <div className="flex items-center gap-2">
                        <div className="flex-1 bg-[#EAE7DF] border-2 border-black h-3 relative">
                          <div className="absolute inset-y-0 left-0 bg-[#C2410C]" style={{ width: `${Math.min(100, r.share_pct)}%` }}/>
                        </div>
                        <span className="font-mono text-xs w-12 text-right">{r.share_pct}%</span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

// Inline tax-rate editor pill — click to edit, blur or Enter to save.
function EventTaxEditor({ event, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(event.tax_rate ?? 0);
  const [saving, setSaving] = useState(false);
  useEffect(() => { setValue(event.tax_rate ?? 0); }, [event.tax_rate]);

  const save = async () => {
    const rate = Math.max(0, parseFloat(value || 0));
    if (rate === Number(event.tax_rate ?? 0)) { setEditing(false); return; }
    setSaving(true);
    try {
      const { data } = await api.put(`/events/${event.event_id}`, { tax_rate: rate });
      onSaved(data);
      toast.success(`Tax updated to ${rate}%`);
    } catch (err) {
      toast.error("Couldn't save tax");
      setValue(event.tax_rate ?? 0);
    } finally { setSaving(false); setEditing(false); }
  };

  if (editing) {
    return (
      <div className="inline-flex items-center gap-2 mt-2" data-testid="event-tax-editor">
        <span className="font-mono text-[10px] uppercase tracking-widest">Tax %</span>
        <input
          type="number"
          min="0" max="100" step="0.001"
          value={value}
          autoFocus
          onChange={(e)=>setValue(e.target.value)}
          onBlur={save}
          onKeyDown={(e) => { if (e.key === "Enter") save(); if (e.key === "Escape") { setValue(event.tax_rate ?? 0); setEditing(false); }}}
          disabled={saving}
          className="brutal-input !py-0 !px-2 !w-24 text-sm"
          data-testid="event-tax-rate-input"
        />
      </div>
    );
  }
  return (
    <button
      type="button"
      onClick={()=>setEditing(true)}
      className="font-mono text-[10px] uppercase tracking-widest text-[#4A4A4A] hover:text-black underline decoration-dotted mt-2 block"
      data-testid="event-tax-edit-trigger"
    >
      Tax {Number(event.tax_rate ?? 0).toFixed(3).replace(/\.?0+$/, "")}% · edit
    </button>
  );
}
